/**
 * Ce que l'administration cherche quand elle tape dans la barre de l'écran
 * des factures.
 *
 * ## Pourquoi une lecture de la saisie, et pas un `ilike` brut
 *
 * Les deux écrans fusionnés cherchaient par numéro, nom et courriel. Il
 * manquait la **communication structurée** — et c'est celle qu'on a sous les
 * yeux quand on cherche une facture depuis un relevé bancaire (§4.4 de la
 * passation). Or elle ne se tape jamais comme elle est stockée :
 *
 * - la banque l'affiche `+++100/2600/12345+++` ;
 * - un relevé PDF la copie parfois `100/2600/12345` ;
 * - la base la stocke `100260012345`, douze chiffres nus.
 *
 * Un `ilike` sur la saisie brute ne trouverait donc **rien** dans les deux
 * premiers cas, tout en affichant « aucune facture ne correspond ». C'est le
 * défaut le plus coûteux qu'un écran de recherche puisse avoir : on en tire
 * une conclusion — « cette facture n'existe pas » — alors qu'elle est là.
 *
 * ## Ce que la lecture reconnaît
 *
 * Trois formes, de la plus sûre à la plus vague :
 *
 * 1. **une communication structurée** — douze chiffres, quelle que soit la
 *    ponctuation. C'est la piste la plus fiable du circuit : la clé modulo 97
 *    est validée par la banque du parent à la saisie, donc elle ne peut pas
 *    être mal recopiée.
 * 2. **un numéro de facture** — complet ou partiel. `2601466`,
 *    `NDSC-2601466` et `CDV-NDSC-2601466` doivent mener à la même facture ;
 *    seul le préfixe `CDV-` manque au deuxième, et c'est un cas réel.
 * 3. **du texte** — un nom, un courriel, un fragment.
 *
 * La forme est **dite à l'écran**. Un écran qui cherche sans annoncer ce qu'il
 * a compris laisse l'utilisateur deviner pourquoi il ne trouve pas.
 */

import { isValidStructured } from '../payments/communication';

/** Trois caractères au minimum : en dessous, un `ilike` rend la liste entière. */
export const MIN_CARACTERES = 3;

export type FormeDeRecherche = 'vide' | 'structuree' | 'numero' | 'texte';

export interface Recherche {
  forme: FormeDeRecherche;
  /** Ce qu'il faut effectivement envoyer à la base. */
  valeur: string;
  /** Ce que la règle a compris, à afficher sous la barre. */
  explication: string | null;
  /**
   * Vrai quand les douze chiffres portent une clé modulo 97 juste.
   *
   * On cherche même quand elle est fausse — une communication mal recopiée
   * reste une piste — mais l'écran peut le signaler, parce qu'une clé fausse
   * veut souvent dire « un chiffre a sauté ».
   */
  cleValide?: boolean;
}

const VIDE: Recherche = { forme: 'vide', valeur: '', explication: null };

/**
 * Les douze chiffres d'une communication structurée, si la saisie en porte.
 *
 * Toute la ponctuation est retirée avant de compter : `+++100/2600/12345+++`,
 * `100/2600/12345` et `100260012345` donnent le même résultat. Exactement
 * douze, pas « au moins douze » : treize chiffres ne sont pas une
 * communication à laquelle il en manque un, c'est autre chose.
 */
export function chiffresStructures(saisie: string): string | null {
  const chiffres = saisie.replace(/\D/g, '');
  return chiffres.length === 12 ? chiffres : null;
}

/**
 * Vrai si la saisie ressemble à un numéro de facture plutôt qu'à un nom.
 *
 * Le critère est la présence d'une suite d'au moins quatre chiffres. En
 * dessous, `26` ou `466` remonteraient des centaines de factures sans rien
 * désigner — et une famille dont le nom contient un chiffre est bien plus
 * rare qu'un fragment de numéro.
 */
export function ressembleAUnNumero(saisie: string): boolean {
  return /\d{4,}/.test(saisie);
}

/**
 * Nettoie une saisie avant de la passer à un `ilike` de PostgREST.
 *
 * Les caractères que PostgREST lit comme de la syntaxe sont **retirés** plutôt
 * qu'échappés : la virgule sépare ses filtres, la parenthèse les groupe, le
 * guillemet et les deux-points délimitent une valeur, et `%` ou `*`
 * deviendraient des jokers silencieux — une recherche qui élargirait toute
 * seule sans le dire.
 *
 * Aucun d'eux n'aide à retrouver une facture : un numéro n'en contient pas, ni
 * un nom, ni une adresse courriel. Les retirer ne perd donc rien, et évite
 * d'avoir à choisir entre deux formes d'échappement selon que le filtre part
 * seul ou dans un `or`.
 */
export function nettoyerPourIlike(saisie: string): string {
  return saisie.replace(/[,()%*\\":]/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Ce que la barre de recherche a compris de ce qu'on y a tapé. */
export function lireRecherche(saisie: string | null | undefined): Recherche {
  const t = (saisie ?? '').trim();
  if (t.length === 0) return VIDE;

  const structuree = chiffresStructures(t);
  if (structuree) {
    const valide = isValidStructured(structuree);
    return {
      forme: 'structuree',
      valeur: structuree,
      cleValide: valide,
      explication: valide
        ? `La communication structurée ${structuree}`
        : `La communication ${structuree} — sa clé de contrôle est fausse, un chiffre a peut-être sauté`,
    };
  }

  if (t.length < MIN_CARACTERES) {
    return {
      forme: 'vide',
      valeur: '',
      explication: `${MIN_CARACTERES} caractères au minimum pour chercher.`,
    };
  }

  const propre = nettoyerPourIlike(t);
  if (propre.length < MIN_CARACTERES) return VIDE;

  if (ressembleAUnNumero(propre)) {
    return {
      forme: 'numero',
      valeur: propre,
      explication: `Les factures dont le numéro contient « ${propre} »`,
    };
  }

  return {
    forme: 'texte',
    valeur: propre,
    explication: `Numéro, nom, courriel ou communication contenant « ${propre} »`,
  };
}
